import React, { Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { useTenant } from './contexts/TenantContext';
import { PageLoader } from './components/common';
import ErrorBoundary from './components/common/ErrorBoundary';
import DashboardLayout from './components/layout/DashboardLayout';

// Lazy-load pages for code splitting
const LoginPage          = lazy(() => import('./pages/auth/LoginPage'));
const RegisterPage       = lazy(() => import('./pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('./pages/auth/ForgotPasswordPage'));
const ResetPasswordPage  = lazy(() => import('./pages/auth/ResetPasswordPage'));
const VerifyEmailPage    = lazy(() => import('./pages/auth/VerifyEmailPage'));
const DashboardPage      = lazy(() => import('./pages/DashboardPage'));
const TicketsPage        = lazy(() => import('./pages/tickets/TicketsPage'));
const TicketDetailPage   = lazy(() => import('./pages/tickets/TicketDetailPage'));
const CreateTicketPage   = lazy(() => import('./pages/tickets/CreateTicketPage'));
const KnowledgeBasePage  = lazy(() => import('./pages/KnowledgeBasePage'));
const ArticleDetailPage  = lazy(() => import('./pages/ArticleDetailPage'));
const KBArticleFormPage  = lazy(() => import('./pages/KBArticleFormPage'));
const SettingsPage       = lazy(() => import('./pages/SettingsPage'));
const UsersPage          = lazy(() => import('./pages/UsersPage'));
const ReportsPage        = lazy(() => import('./pages/ReportsPage'));
const ProfilePage        = lazy(() => import('./pages/ProfilePage'));
const LiveChatPage       = lazy(() => import('./pages/LiveChatPage'));
const NotFoundPage       = lazy(() => import('./pages/NotFoundPage'));
const AuditLogPage       = lazy(() => import('./pages/AuditLogPage'));
const WebhooksPage       = lazy(() => import('./pages/WebhooksPage'));
const AgentGroupsPage    = lazy(() => import('./pages/AgentGroupsPage'));
const TicketTemplatesPage = lazy(() => import('./pages/TicketTemplatesPage'));
const CategoriesPage     = lazy(() => import('./pages/CategoriesPage'));
const EmailTemplatesPage = lazy(() => import('./pages/EmailTemplatesPage'));
const DepartmentDashboard = lazy(() => import('./pages/DepartmentDashboard'));

// ─── Route Guards ─────────────────────────────────────────────────────────────

/** Redirect to login if not authenticated */
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) return <PageLoader message="Authenticating..." />;
  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }
  return children;
};

/** Redirect to dashboard if already logged in */
const GuestRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();

  if (loading) return <PageLoader message="Loading..." />;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

/** Require minimum role */
const RoleRoute = ({ children, minRole = 'customer' }) => {
  const { hasMinRole } = useAuth();
  if (!hasMinRole(minRole)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// ─── Tenant loading screen ────────────────────────────────────────────────────
const TenantLoader = ({ children }) => {
  const { loading, error } = useTenant();
  if (loading) return <PageLoader message="Loading your workspace..." />;
  if (error) {
    // Non-fatal: show a warning banner but don't block the app
    console.warn('Tenant config error:', error);
  }
  return children;
};

// ─── App ──────────────────────────────────────────────────────────────────────
const App = () => {
  return (
    <ErrorBoundary>
    <TenantLoader>
      <Suspense fallback={<PageLoader />}>
        <Routes>
          {/* ── Public / Guest routes ── */}
          <Route path="/login" element={<GuestRoute><LoginPage /></GuestRoute>} />
          <Route path="/register" element={<GuestRoute><RegisterPage /></GuestRoute>} />
          <Route path="/forgot-password" element={<GuestRoute><ForgotPasswordPage /></GuestRoute>} />
          <Route path="/reset-password" element={<GuestRoute><ResetPasswordPage /></GuestRoute>} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />

          {/* ── Protected app layout ── */}
          <Route path="/" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
            {/* Redirect root → dashboard */}
            <Route index element={<Navigate to="/dashboard" replace />} />

            {/* Dashboard */}
            <Route path="dashboard" element={<DashboardPage />} />

            {/* Tickets — customers see "my tickets", agents see all */}
            <Route path="tickets" element={<TicketsPage />} />
            <Route path="all-tickets" element={
              <RoleRoute minRole="agent"><TicketsPage /></RoleRoute>
            } />
            <Route path="tickets/new" element={<CreateTicketPage />} />
            <Route path="tickets/:id" element={<TicketDetailPage />} />

            {/* Knowledge Base */}
            <Route path="knowledge-base" element={<KnowledgeBasePage />} />
            <Route path="knowledge-base/new" element={
              <RoleRoute minRole="agent"><KBArticleFormPage /></RoleRoute>
            } />
            <Route path="knowledge-base/:id/edit" element={
              <RoleRoute minRole="agent"><KBArticleFormPage /></RoleRoute>
            } />
            <Route path="knowledge-base/:slug" element={<ArticleDetailPage />} />

            {/* Profile */}
            <Route path="profile" element={<ProfilePage />} />

            {/* Live Chat — agents and above */}
            <Route path="live-chat" element={
              <RoleRoute minRole="agent"><LiveChatPage /></RoleRoute>
            } />

            {/* Reports — agents and above */}
            <Route path="reports" element={
              <RoleRoute minRole="agent"><ReportsPage /></RoleRoute>
            } />

            {/* Admin */}
            <Route path="settings" element={
              <RoleRoute minRole="admin"><SettingsPage /></RoleRoute>
            } />
            <Route path="users" element={
              <RoleRoute minRole="admin"><UsersPage /></RoleRoute>
            } />
            <Route path="audit-logs" element={
              <RoleRoute minRole="admin"><AuditLogPage /></RoleRoute>
            } />
            <Route path="webhooks" element={
              <RoleRoute minRole="admin"><WebhooksPage /></RoleRoute>
            } />
            <Route path="agent-groups" element={
              <RoleRoute minRole="admin"><AgentGroupsPage /></RoleRoute>
            } />
            <Route path="ticket-templates" element={
              <RoleRoute minRole="agent"><TicketTemplatesPage /></RoleRoute>
            } />
            <Route path="categories" element={
              <RoleRoute minRole="admin"><CategoriesPage /></RoleRoute>
            } />
            <Route path="email-templates" element={
              <RoleRoute minRole="admin"><EmailTemplatesPage /></RoleRoute>
            } />
            <Route path="departments/:department" element={
              <RoleRoute minRole="agent"><DepartmentDashboard /></RoleRoute>
            } />
          </Route>

          {/* Catch-all */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </Suspense>
    </TenantLoader>
    </ErrorBoundary>
  );
};

export default App;
